/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Longform Article Context Chaining Generation Engine (Task 10.2.4)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import {
  useLongformWizardStore,
  type ArticleHeading,
} from '../stores/useLongformWizardStore';
import { useWalletStore } from '../stores/useWalletStore';

export interface GenerationOptions {
  modelId?: string;
  temperature?: number;
  chunkDelayMs?: number;
  onSectionStart?: (heading: ArticleHeading, index: number, total: number) => void;
  onSectionDelta?: (headingId: string, delta: string) => void;
  onSectionDone?: (heading: ArticleHeading, wordCount: number) => void;
  onComplete?: (fullMarkdown: string, totalWords: number) => void;
  onError?: (err: Error) => void;
}

export class LongformGenerationService {
  private abortController: AbortController | null = null;
  private isPaused = false;
  private pauseResolver: (() => void) | null = null;
  private isRunning = false;

  public getIsRunning(): boolean {
    return this.isRunning;
  }

  public getIsPaused(): boolean {
    return this.isPaused;
  }

  public pause(): void {
    if (!this.isRunning || this.isPaused) return;
    this.isPaused = true;
    useLongformWizardStore.getState().updateGenerationState({
      isPaused: true,
      statusMessage: 'Đã tạm dừng quá trình sinh bài viết.',
    });
  }

  public resume(): void {
    if (!this.isRunning || !this.isPaused) return;
    this.isPaused = false;
    useLongformWizardStore.getState().updateGenerationState({
      isPaused: false,
      statusMessage: 'Tiếp tục sinh bài viết...',
    });
    if (this.pauseResolver) {
      this.pauseResolver();
      this.pauseResolver = null;
    }
  }

  public abort(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.isRunning = false;
    this.isPaused = false;
    if (this.pauseResolver) {
      this.pauseResolver();
      this.pauseResolver = null;
    }
    useLongformWizardStore.getState().updateGenerationState({
      isGenerating: false,
      isPaused: false,
      statusMessage: 'Đã hủy quá trình sinh bài viết.',
    });
  }

  private async waitIfPaused(): Promise<void> {
    if (!this.isPaused) return;
    await new Promise<void>((resolve) => {
      this.pauseResolver = resolve;
    });
  }

  /**
   * Generates realistic, contextual markdown content for a specific heading section.
   */
  private generateSectionContentMock(
    heading: ArticleHeading,
    articleTitle: string,
    primaryKeyword: string,
    brandVoiceTone: string,
    language: string,
    previousSummary: string
  ): string {
    const isEn = language === 'en';
    const kw = primaryKeyword || 'AI';
    const tag = heading.level === 2 ? '##' : '###';

    if (isEn) {
      return (
        `${tag} ${heading.title}\n\n` +
        `When exploring **${articleTitle}**, understanding the core tenets of ${kw} is essential. ` +
        `Recent engineering benchmarks demonstrate that integrating modern edge architectures yields an average 3.4x performance improvement while significantly lowering latency.\n\n` +
        `Key aspects to consider in this section:\n` +
        `- **Architectural Scalability:** Handling distributed workloads without centralized bottlenecks.\n` +
        `- **Data Consistency:** Ensuring atomic synchronization across global points of presence.\n` +
        `- **Cost Optimization:** Reducing egress and idle compute overhead down to zero.\n\n` +
        `> **Expert Insight:** In practical production environments, teams that align with authoritative patterns minimize technical debt by over 40% within the first 90 days.\n\n` +
        `As we build upon ${previousSummary ? `the earlier discussion (${previousSummary})` : 'these foundational principles'}, ` +
        `the focus naturally shifts toward concrete execution and measurable milestones.`
      );
    }

    // Default Vietnamese
    return (
      `${tag} ${heading.title}\n\n` +
      `Trong bối cảnh phát triển mạnh mẽ của **${articleTitle}**, việc làm chủ kỹ thuật về **${kw}** đóng vai trò then chốt cho sự thành bại của dự án. ` +
      `Các báo cáo thực nghiệm mới nhất chỉ ra rằng việc áp dụng kiến trúc chuẩn mực giúp tối ưu hóa hơn 68% chi phí vận hành và tăng tốc độ xử lý lên gấp 3 lần.\n\n` +
      `Các luận điểm cốt lõi cần đặc biệt lưu ý trong phần này bao gồm:\n` +
      `1. **Tính linh hoạt & Khả năng mở rộng:** Đáp ứng đồng thời hàng chục ngàn kết nối mà không làm tắc nghẽn tài nguyên.\n` +
      `2. **Độ an toàn & Tính toàn vẹn:** Đảm bảo các giao dịch được xác thực nghiêm ngặt theo thời gian thực.\n` +
      `3. **Tối ưu hóa trải nghiệm người dùng:** Rút ngắn thời gian phản hồi (TTFT) xuống dưới mức mắt người có thể cảm nhận.\n\n` +
      `> 💡 **Khuyến nghị từ chuyên gia (${brandVoiceTone}):** Không nên vội vàng áp dụng cấu hình phức tạp ngay từ đầu. Hãy bắt đầu từ cấu trúc tinh gọn, kiểm thử tải kỹ lưỡng rồi mới tiến hành nhân rộng.\n\n` +
      `${previousSummary ? `Kế thừa từ những luận điểm ở phần trước (${previousSummary}), ` : ''}` +
      `chúng ta có đầy đủ cơ sở để hiện thực hóa các bước hành động tiếp theo một cách chuẩn xác và hiệu quả nhất.`
    );
  }

  /**
   * Executes the 4-step Context Chaining generation pipeline sequentially.
   */
  public async startGeneration(options: GenerationOptions = {}): Promise<void> {
    if (this.isRunning) {
      this.abort();
    }

    const store = useLongformWizardStore.getState();
    const headings = store.headings;
    if (!headings || headings.length === 0) {
      throw new Error('Dàn ý bài viết chưa được thiết lập. Vui lòng hoàn thành Bước 3 trước.');
    }

    const title = store.getSelectedFinalTitle();
    const { primaryKeyword, language, brandVoiceId } = store.setup;
    const brandVoice = brandVoiceId;

    this.isRunning = true;
    this.isPaused = false;
    this.abortController = new AbortController();

    // 1. Pre-flight Wallet Deduction
    const estimatedTokens = headings.length * 350;
    const estimatedCredits = Math.max(1, Math.ceil(estimatedTokens / 500));
    const walletStore = useWalletStore.getState();
    const deductRes = walletStore.optimisticDeduct(estimatedCredits);

    if (!deductRes.success && walletStore.balance <= 0) {
      this.isRunning = false;
      const err = new Error('Số dư tín chỉ ví không đủ để khởi chạy sinh bài viết dài.');
      options.onError?.(err);
      throw err;
    }

    store.updateGenerationState({
      isGenerating: true,
      isPaused: false,
      progressPercent: 0,
      currentSectionIndex: 0,
      totalSections: headings.length,
      currentHeadingTitle: headings[0]?.title || '',
      generatedSections: {},
      fullArticleMarkdown: '',
      totalWords: 0,
      statusMessage: `Bắt đầu sinh bài viết dài (${headings.length} mục)...`,
      error: null,
    });

    let previousSummary = '';
    const collectedSections: string[] = [];

    try {
      for (let i = 0; i < headings.length; i++) {
        if (this.abortController?.signal.aborted) break;
        await this.waitIfPaused();

        const currentHeading = headings[i]!;

        // Update progress state
        const progress = Math.round((i / headings.length) * 100);
        store.updateGenerationState({
          currentSectionIndex: i,
          currentHeadingTitle: currentHeading.title,
          progressPercent: progress,
          statusMessage: `Đang sinh mục ${i + 1}/${headings.length}: ${currentHeading.title}`,
        });

        options.onSectionStart?.(currentHeading, i, headings.length);

        // Generate section content with streaming simulation or Edge SSE
        const fullSectionText = this.generateSectionContentMock(
          currentHeading,
          title,
          primaryKeyword,
          brandVoice,
          language,
          previousSummary
        );

        // Stream in small token chunks
        const chunks = fullSectionText.split(' ');
        let accumulated = '';
        const delay = options.chunkDelayMs !== undefined ? options.chunkDelayMs : 10;

        for (let cIdx = 0; cIdx < chunks.length; cIdx++) {
          if (this.abortController?.signal.aborted) break;
          await this.waitIfPaused();

          const word = chunks[cIdx] + (cIdx < chunks.length - 1 ? ' ' : '');
          accumulated += word;
          store.appendSectionDelta(currentHeading.id, word);
          options.onSectionDelta?.(currentHeading.id, word);

          // Stream speed control
          if (delay > 0) {
            await new Promise((r) => setTimeout(r, delay));
          }
        }

        collectedSections.push(accumulated);
        const sectionWords = accumulated.split(/\s+/).filter(Boolean).length;
        options.onSectionDone?.(currentHeading, sectionWords);

        // Extract summary for context chaining
        previousSummary = currentHeading.title;

        // Brief breather between sections
        if (delay > 0) {
          await new Promise((r) => setTimeout(r, 50));
        }
      }

      // If aborted, stop here
      if (this.abortController?.signal.aborted) {
        return;
      }

      // 2. Assemble Full Article Markdown
      const fullArticleMarkdown =
        `# ${title}\n\n` +
        `> **Chủ đề:** ${store.setup.topic || title} | **Từ khóa chính:** \`${primaryKeyword}\`\n\n` +
        `---\n\n` +
        collectedSections.join('\n\n---\n\n');

      const totalWords = fullArticleMarkdown.split(/\s+/).filter(Boolean).length;

      store.finalizeFullArticle(fullArticleMarkdown, totalWords);
      options.onComplete?.(fullArticleMarkdown, totalWords);
    } catch (err: unknown) {
      const error = err instanceof Error ? err : new Error(String(err));
      store.updateGenerationState({
        isGenerating: false,
        error: error.message,
        statusMessage: `Lỗi: ${error.message}`,
      });
      options.onError?.(error);
    } finally {
      this.isRunning = false;
      this.isPaused = false;
    }
  }
}

export const longformGenerationService = new LongformGenerationService();
